import { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaFacebook, FaInstagram, FaLinkedinIn, FaTiktok, FaYoutube, FaPhone, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import ApplyLink from './ApplyLink';
import { trackEvent } from '../lib/analytics';
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, whatsappLink } from '../lib/contact';
import { LOCALES } from '../locales/registry.mjs';
// 160×160 variant covers both the 78-92 px navbar logo and the 88 px
// drawer logo at 2× DPR. Imported via webpack so the bundled URL gets a
// content hash (e.g. /static/media/logo-160.4a3f7c.webp). Hashed paths
// can never get poisoned by the SPA catch-all in _redirects (different
// URL per build), so this image is structurally immune to the
// cache-poisoning class of bug we hit in April 2026 on the unhashed
// /images/brand/ path.
import logoSrc from '../assets/brand/logo-160.webp';
import './Navbar.css';

// Aliased so existing references (LANGUAGES.map / LANGUAGES.find) keep
// working without churn. Display metadata (label, name, flag) lives on
// the registry entries directly, so adding a new locale only updates
// one file.
const LANGUAGES = LOCALES;


const NAV_KEYS = [
  { to: '/',             key: 'nav_home' },
  { to: '/about',        key: 'nav_about' },
  { to: '/find-course',  key: 'nav_find_course' },
  { to: '/services',     key: 'nav_services' },
  { to: '/student-guides', key: 'nav_student_guides' },
  { to: '/blog',         key: 'nav_blog' },
  { to: '/work-with-us', key: 'nav_work_with_us' },
  { to: '/contact',      key: 'nav_contact' },
];

// Student + Staff Login buttons. Gated on REACT_APP_PORTALS_LIVE so the
// website doesn't send visitors to a not-yet-deployed CRM:
//   • Flag unset / falsy → buttons go to /student-portal + /agent-portal,
//                          which render the in-website "Coming Soon" page.
//                          Use this until the CRM auth flow is live.
//   • Flag = "true"      → buttons go to ${CRM_URL}/student-login and
//                          ${CRM_URL}/login on the real CRM Next.js app.
// Flip the flag in .env when the CRM portals are actually serving traffic.
const CRM_URL = process.env.REACT_APP_CRM_URL || '';
const PORTALS_LIVE = process.env.REACT_APP_PORTALS_LIVE === 'true' && !!CRM_URL;
const STUDENT_LOGIN_HREF = PORTALS_LIVE ? `${CRM_URL}/student-login` : '/student-portal';
const STAFF_LOGIN_HREF   = PORTALS_LIVE ? `${CRM_URL}/login`         : '/agent-portal';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [langOpen,   setLangOpen]     = useState(false);
  const [navHidden,  setNavHidden]    = useState(false);

  // Refs for the drawer focus-management dance:
  //   • burgerRef     — the trigger button. Focus returns here when
  //                     the drawer closes (WCAG 2.4.3 Focus Order).
  //   • drawerRef     — the drawer container. Used to scope the Tab
  //                     focus-trap to focusable descendants only.
  //   • closeBtnRef   — the X button inside the drawer. Focus moves
  //                     here when the drawer opens — predictable and
  //                     a sane default; the user is one Tab away from
  //                     the first nav link, or Enter/Space dismisses.
  const burgerRef    = useRef(null);
  const drawerRef    = useRef(null);
  const closeBtnRef  = useRef(null);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY && y > 80) {
        setNavHidden(true);
        document.documentElement.style.setProperty('--sticky-offset', '0px');
      } else {
        setNavHidden(false);
        document.documentElement.style.setProperty('--sticky-offset', '96px');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Mobile drawer — toggle a body attribute so other UI (the promo
  // announcement bar) can react via pure CSS. On mobile the drawer
  // covers the full viewport and the announcement bar would otherwise
  // overlap the drawer's logo header (the bar is in document flow at
  // top:0, the drawer is fixed at top:0 — they collide). Hiding the
  // bar while the drawer is open is the cleanest fix; the promo
  // re-appears when the drawer closes. Also lock body scroll so the
  // page underneath doesn't move when the user scrolls inside the
  // drawer.
  useEffect(() => {
    if (drawerOpen) {
      document.body.setAttribute('data-drawer-open', 'true');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.removeAttribute('data-drawer-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.removeAttribute('data-drawer-open');
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // ─── Drawer keyboard / focus management (WCAG 2.4.3, 2.1.2) ───────
  // When the drawer opens:
  //   1. Move keyboard focus INTO the drawer (close button) so the
  //      next Tab lands inside it, not on whatever was focused before.
  //   2. ESC closes the drawer (matches platform-standard modal UX).
  //   3. Tab is trapped within the drawer's focusable descendants —
  //      the user can't accidentally tab to elements behind the
  //      semi-opaque overlay (which would be confusing because those
  //      elements are visually obscured but still receive focus).
  // When the drawer closes:
  //   4. Focus returns to the burger button (the trigger), so a
  //      keyboard user doesn't lose their place in the page.
  //
  // The implementation keeps everything in one useEffect so the
  // attach/detach lifecycle is symmetric — there's no path where the
  // listener leaks past close. Refs (instead of querying the DOM
  // anew each keystroke) keep the per-keypress cost trivial.
  useEffect(() => {
    if (!drawerOpen) return;

    // 1. Initial focus into the drawer.
    closeBtnRef.current?.focus();

    // Capture the ref node now so the cleanup function uses the same
    // node that was focused on open — by the time cleanup runs the
    // ref might have been re-assigned to a different DOM node (e.g.
    // if the burger re-mounted), and react-hooks/exhaustive-deps
    // flags this as a stale-ref bug. Snapshotting fixes the warning
    // and is also semantically correct: we want to return focus to
    // the burger that opened this drawer, not whatever's there later.
    const burgerEl = burgerRef.current;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrawerOpen(false);
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) return;

      // 3. Focus trap — keep Tab cycling within the drawer.
      // Selector matches the canonical "focusable" pattern. We don't
      // include [contenteditable] or audio/video[controls] because
      // there are none of those inside the drawer; if any are added
      // later, expand the selector accordingly.
      const focusables = drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // 4. Return focus to the burger on close. Only fires when
      // drawerOpen flips true → false (or on unmount while open).
      // Use the captured node, not burgerRef.current, per the
      // explanation above.
      burgerEl?.focus();
    };
  }, [drawerOpen]);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const switchLang = code => {
    trackEvent('language_switch', { from: i18n.language, to: code });
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const trackPhoneClick = (source) => () => trackEvent('phone_click', { source });
  const trackWhatsAppClick = (source) => () => trackEvent('whatsapp_click', { source });

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      {/* ── Mobile contact/social strip (hidden on desktop) ── */}
      <div className="mobile-topstrip">
        <div className="mts-contact">
          <a href={`tel:${PHONE_TEL}`} aria-label="Phone" onClick={trackPhoneClick('mobile_topstrip')}><FaPhone /></a>
          <div className="mts-divider" />
          <a href={`mailto:${EMAIL}`} aria-label="Email"><FaEnvelope /></a>
        </div>
        <div className="mts-social">
          <a href="https://www.facebook.com/profile.php?id=61559180439503" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebook /></a>
          <a href="https://www.youtube.com/@academyeducationnetwork" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FaYoutube /></a>
          <a href="https://www.instagram.com/academy_education_network/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></a>
          <a href="https://www.linkedin.com/company/103781108" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedinIn /></a>
          <a href="https://tiktok.com/@academyeducationnetwork" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><FaTiktok /></a>
        </div>
      </div>

      {/* ── Top accent bar ─────────────────────────────────── */}
      <div className="navbar-topbar">
        <div className="navbar-topbar-inner">
          <div className="topbar-social">
            <a href="https://www.facebook.com/profile.php?id=61559180439503" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebook /></a>
            <a href="https://www.instagram.com/academy_education_network/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></a>
            <a href="https://www.linkedin.com/company/103781108" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedinIn /></a>
            <a href="https://tiktok.com/@academyeducationnetwork" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><FaTiktok /></a>
            <a href="https://youtube.com/@academyeducationnetwork" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FaYoutube /></a>
          </div>
          <div className="topbar-right">
            {/* Contact icons */}
            <div className="topbar-contact">
              <a href={`tel:${PHONE_TEL}`} aria-label="Phone" className="topbar-contact-icon topbar-contact-phone" onClick={trackPhoneClick('desktop_topbar')}><FaPhone /></a>
              <a href={`mailto:${EMAIL}`} aria-label="Email" className="topbar-contact-icon"><FaEnvelope /></a>
            </div>
            <span className="topbar-divider" />
            {/* Language switcher */}
            <div className="lang-switcher">
              <button
                className="lang-toggle"
                onClick={() => setLangOpen(o => !o)}
                /* No aria-label — visible text "🇬🇧 EN ▾" serves as the
                 * accessible name. axe + Lighthouse flagged the previous
                 * `aria-label="Switch language"` as a label-content-name-
                 * mismatch (WCAG 2.5.3 Label in Name) because voice-control
                 * users saying "click EN" couldn't activate the button —
                 * the accessible name didn't contain the visible "EN".
                 * aria-haspopup tells SR users this button opens a menu;
                 * aria-expanded reflects the open/closed state. */
                aria-haspopup="listbox"
                aria-expanded={langOpen}
              >
                {currentLang.flag} {currentLang.label} ▾
              </button>
              {langOpen && (
                <>
                  <div className="lang-backdrop" onClick={() => setLangOpen(false)} />
                  <div className="lang-dropdown">
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        className={`lang-option${lang.code === i18n.language ? ' active' : ''}`}
                        onClick={() => switchLang(lang.code)}
                      >
                        <span className="lang-flag">{lang.flag}</span>
                        <span className="lang-code">{lang.label}</span>
                        <span className="lang-name">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href={STUDENT_LOGIN_HREF}
              className="topbar-btn"
              onClick={() => trackEvent('login_click', { type: 'student', source: 'topbar' })}
            >
              Student Login
            </a>
            <a
              href={STAFF_LOGIN_HREF}
              className="topbar-btn topbar-btn-gold"
              onClick={() => trackEvent('login_click', { type: 'staff', source: 'topbar' })}
            >
              Staff Login
            </a>
          </div>
        </div>
      </div>

      {/* ── Main nav ───────────────────────────────────────── */}
      <nav className={`navbar${navHidden ? ' navbar--hidden' : ''}`}>
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo" aria-label="Academy Education Network">
            <div
              className="navbar-logo-bg"
              style={{ backgroundImage: `url(${logoSrc})` }}
              role="img"
              aria-label="Academy Education Network logo"
            />
          </Link>

          <ul className="nav-links desktop-only">
            {NAV_KEYS.map(({ to, key }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                  end={to === '/'}
                >
                  {t(key)}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="navbar-actions desktop-only">
            <ApplyLink className="btn-apply-now">Apply Now</ApplyLink>
          </div>

          {/* Burger.
           *   aria-expanded reflects drawer state for SR users
           *   aria-controls names the drawer the button governs
           *   ref captures the element so we can return focus to it
           *     when the drawer closes (see keyboard-management
           *     useEffect above). */}
          <button
            ref={burgerRef}
            className="burger mobile-only"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="primary-mobile-drawer"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── Mobile drawer ────────────────────────────────────
       *   role="dialog" + aria-modal="true" tells SR users this is
       *     a modal context — they get the "you're in a modal"
       *     announcement and any virtual cursor stays in-bounds.
       *   aria-labelledby points at the drawer logo so SR users
       *     hear "Academy Education Network" as the dialog name
       *     when it opens (rather than the generic "dialog").
       *   id matches the burger's aria-controls reference. */}
      {drawerOpen && (
        <>
          <div className="drawer-overlay" onClick={closeDrawer} />
          <aside
            ref={drawerRef}
            id="primary-mobile-drawer"
            className="drawer mobile-only"
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-logo-name"
          >

            {/* Header */}
            <div className="drawer-header">
              <div className="drawer-header-spacer" />
              <Link to="/" className="drawer-logo" onClick={closeDrawer}>
                <img id="drawer-logo-name" src={logoSrc} alt="Academy Education Network" width="500" height="500" loading="lazy" decoding="async" />
              </Link>
              <button ref={closeBtnRef} className="drawer-close" onClick={closeDrawer} aria-label="Close menu">✕</button>
            </div>

            {/* Nav links */}
            <ul className="drawer-nav">
              {NAV_KEYS.map(({ to, key }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) => isActive ? 'drawer-nav-active' : ''}
                    onClick={closeDrawer}
                  >
                    {t(key)}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Apply CTA */}
            <ApplyLink className="drawer-apply" onClick={closeDrawer}>
              Apply Now
            </ApplyLink>

            {/* Language */}
            <div className="drawer-lang">
              <p className="drawer-section-label">Website Language</p>
              <div className="drawer-lang-grid">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    className={`drawer-lang-btn${lang.code === i18n.language ? ' active' : ''}`}
                    onClick={() => { switchLang(lang.code); closeDrawer(); }}
                  >
                    <span className="dlb-flag">{lang.flag}</span>
                    <span className="dlb-code">{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Account + contact */}
            <div className="drawer-footer">
              <a
                href={STUDENT_LOGIN_HREF}
                className="drawer-login-btn"
                onClick={() => { trackEvent('login_click', { type: 'student', source: 'drawer' }); closeDrawer(); }}
              >Student Login</a>
              <a
                href={STAFF_LOGIN_HREF}
                className="drawer-login-btn drawer-login-btn--gold"
                onClick={() => { trackEvent('login_click', { type: 'staff', source: 'drawer' }); closeDrawer(); }}
              >Staff Login</a>
              <a href={`tel:${PHONE_TEL}`} className="drawer-phone" onClick={trackPhoneClick('mobile_drawer')}>📞 {PHONE_DISPLAY}</a>
              <a href={whatsappLink()} className="drawer-phone" target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppClick('mobile_drawer')}><FaWhatsapp /> WhatsApp Us</a>
              <div className="drawer-social">
                <a href="https://www.facebook.com/profile.php?id=61559180439503" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebook /></a>
                <a href="https://www.instagram.com/academy_education_network/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></a>
                <a href="https://www.linkedin.com/company/103781108" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedinIn /></a>
                <a href="https://tiktok.com/@academyeducationnetwork" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><FaTiktok /></a>
                <a href="https://youtube.com/@academyeducationnetwork" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FaYoutube /></a>
              </div>
            </div>

          </aside>
        </>
      )}
    </>
  );
}
